import { Building2 } from 'lucide-react';
import type { ReactElement } from 'react';
import type { Workspace } from '@molt/shared';
import { cn } from '@/lib/cn';
import { Menu, type MenuItem, type MenuSection } from '@/components/Menu';
import {
  useInterfaceLanguage,
  type InterfaceLanguageContextValue,
} from '@/lib/interface-language';

/**
 * `FE-07.3` — the workspace switcher. Chunk `D2`, agent F. `FR-PERM-004` AC3.
 *
 * ---------------------------------------------------------------------------
 * THIS ITEM DID NOT EXIST BEFORE THIS CHUNK
 * ---------------------------------------------------------------------------
 * `FR-PERM-004` AC3 — *"given a user creates a second workspace, when they
 * switch between them, then board lists and member lists are fully separate"* —
 * was claimed by **no `FE-` item's text**. `entry-surfaces.md` **C9** flagged
 * it at design time; `D1` met it, left `Header`'s `workspaceSwitcherSlot`
 * empty, and asserted in a composition test that `account` was the only filled
 * slot, so that absorbing it silently would turn something red.
 * `D2-BRIEF.md` §2.1 takes the decision and gives it this number.
 *
 * ⚠ **It lives in this file and no other**, which is the reversibility
 * condition that decision was taken under.
 *
 * ---------------------------------------------------------------------------
 * NAVIGATION THROUGH `Menu` IS A CALLBACK, NOT A LINK
 * ---------------------------------------------------------------------------
 * `Menu` has no `asChild` and no `href`, and Radix activates an item by calling
 * `click()` on the **item itself**, never on a descendant — so a `Link` inside
 * a `MenuItem` works for a mouse and **silently does nothing for a keyboard**.
 * `AccountMenu`'s `onOpenProfile` is the shipped precedent and this component
 * follows it. `__root.tsx` and `AccountMenu.tsx` both name this component as
 * the second place that meets it. **`D4` needs the same answer.**
 *
 * ── One activation channel, not two ─────────────────────────────────────────
 * Radix's `MenuRadioItem` composes the *item's own* `onSelect` with the
 * *section's* `onValueChange` — both fire on one activation. `Menu.test.tsx`'s
 * own `Switcher` fixture wires items with a no-op `onSelect` and does the real
 * work in `onValueChange`, and this file copies that rather than wiring both,
 * which would call `props.onSelect` twice per click.
 *
 * ── The trigger's visible label and accessible name diverge on purpose ─────
 * `D2-BRIEF.md` §6.6 rule 4 / §7 rule 3: below 640px the trigger renders
 * icon-only (the visible name moves out of the trigger) but the `aria-label`
 * stays `Workspace: {name}` at every width — otherwise a spec locator that
 * works at one viewport silently stops working at another. This file cannot
 * assert the viewport swap itself (`jsdom` computes no layout); the structural
 * proxy — `aria-label` fixed on the button, the visible span always
 * `aria-hidden` — is what the unit test checks. The breakpoint swap itself is
 * agent J's to assert in a real browser.
 *
 * ── The radio value needs a sentinel ────────────────────────────────────────
 * `Menu`'s `MenuSection.value`/`onValueChange` work over `MenuItem.id`, a
 * plain string, and `currentWorkspaceId` is `string | null`. `ALL_ID` below is
 * the sentinel that stands in for `null` inside `Menu`'s vocabulary only; it
 * never crosses this file's boundary — `onSelect` always receives a real id or
 * `null`.
 *
 * ── An unresolvable `currentWorkspaceId` degrades to "All workspaces" ──────
 * §5.4's warning generalises here: `currentWorkspaceId` is a caller-supplied
 * prop, not a value this component can trust is still a member of
 * `workspaces` (a `?workspace=` id for a workspace the caller lost access to,
 * for instance). Every derived value — the trigger label, the checked radio,
 * and whether "Members…" is enabled — is computed from the **resolved**
 * workspace (`current`, found by id in `workspaces`), not from the raw id.
 * This widens §6.6 rule 3's stated predicate (`currentWorkspaceId === null`)
 * to "no workspace in `workspaces` has this id" — reported in the chunk
 * return as a deliberate deviation.
 */

export interface WorkspaceSwitcherProps {
  workspaces: readonly Workspace[];
  /** `null` is "All workspaces", and it is the default. */
  currentWorkspaceId: string | null;
  onSelect: (workspaceId: string | null) => void;
  onOpenMembers: (workspaceId: string) => void;
  onOpenActivity: (workspaceId: string) => void;
  onCreateWorkspace: () => void;
  /**
   * Forwarded to the underlying `Menu`.
   *
   * ⚠ **Added in D2's integration pass, because a caller outside this file had
   * no way to reach it.** *New workspace…* opens a `Dialog` with a text field
   * in it, and `C2` recorded that a menu item which hands focus to something
   * you type into needs `onCloseAutoFocus` — without it Radix returns focus to
   * the trigger as the menu closes and the keyboard member types into nothing.
   *
   * It is not currently load-bearing, and that was **measured rather than
   * assumed**: `Dialog`'s own Radix `FocusScope` traps focus and pulls it back
   * the instant the closing menu tries to move it, so focus stays in the name
   * field with or without this. That protection is **accidental and does not
   * generalise** — the first caller that opens a non-trapping surface from this
   * menu loses it — so the prop exists rather than the composition relying on a
   * second component's side effect.
   */
  onCloseAutoFocus?: (event: Event) => void;
  className?: string;
}
/**
 * `FE-80.1` — a module-level constant cannot call `t()`, so it holds the
 * **key** and the component resolves it at render. `BoardMapView.tsx:80-84`
 * is the precedent and states the reason: a constant holding the *English of
 * the moment* leaves the previous language on screen when a catalogue arrives
 * underneath it, and no test in this repository can see that.
 *
 * ⚠ A key held in a constant is **invisible to a `t('…')` grep**. The deleted
 * `ALL_WORKSPACES_LABEL` residual proved why the pin needs both sides: this
 * literal must survive, and source-held English must stay absent.
 */
export const ALL_WORKSPACES_KEY = 'workspaceSwitcher.allWorkspaces';

/** Stands in for `currentWorkspaceId === null` inside `Menu`'s id vocabulary. */
const ALL_ID = '__all__';

type Translate = InterfaceLanguageContextValue['t'];

/**
 * `workspaceSchema.name` has `.max()` and no `.min()` — see §5.6.
 *
 * ⚠ Takes `t` as a parameter rather than reading a module constant, which is
 * the other half of the `ALL_WORKSPACES_KEY` decision: this helper is
 * module-private and had exactly one caller, so threading the translator in
 * costs nothing and keeps the fallback visible to a `t('…')` grep.
 */
function workspaceName(t: Translate, workspace: Workspace): string {
  return workspace.name === ''
    ? t('workspaceSwitcher.untitledWorkspace')
    : workspace.name;
}

export function WorkspaceSwitcher({
  workspaces,
  currentWorkspaceId,
  onSelect,
  onOpenMembers,
  onOpenActivity,
  onCreateWorkspace,
  onCloseAutoFocus,
  className,
}: WorkspaceSwitcherProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const current = workspaces.find((w) => w.id === currentWorkspaceId) ?? null;
  const triggerLabel =
    current === null ? t(ALL_WORKSPACES_KEY) : workspaceName(t, current);
  const accessibleName = t('workspaceSwitcher.trigger', { name: triggerLabel });

  const newWorkspaceItem: MenuItem = {
    id: 'new-workspace',
    label: t('workspaceSwitcher.newWorkspace'),
    onSelect: onCreateWorkspace,
  };

  let sections: MenuSection[];
  // `Menu` already guards `footerItems && footerItems.length > 0`, so an
  // empty array (rather than `undefined`) renders nothing extra and needs no
  // conditional spread for `exactOptionalPropertyTypes`.
  let footerItems: MenuItem[];

  if (workspaces.length === 0) {
    // §6.6 rule 6: no workspace exists to switch between or to open members
    // for, so the radio group and "Members…" are both omitted rather than
    // rendered degenerate/disabled — the menu's only content is the one
    // action that gets the member out of this state. Folded into `sections`
    // (not `footerItems`) so `Menu` renders no orphan leading separator.
    sections = [{ id: 'actions', items: [newWorkspaceItem] }];
    footerItems = [];
  } else {
    const items: MenuItem[] = [
      { id: ALL_ID, label: t(ALL_WORKSPACES_KEY), onSelect: () => undefined },
      ...workspaces.map((workspace): MenuItem => ({
        id: workspace.id,
        label: workspaceName(t, workspace),
        onSelect: () => undefined,
      })),
    ];
    sections = [
      {
        id: 'workspaces',
        selection: 'radio',
        value: current?.id ?? ALL_ID,
        onValueChange: (id) => onSelect(id === ALL_ID ? null : id),
        items,
      },
    ];
    footerItems = [
      {
        id: 'activity',
        label: t('workspaceSwitcher.activity'),
        disabled: current === null,
        onSelect: () => {
          if (current !== null) {
            onOpenActivity(current.id);
          }
        },
      },
      {
        id: 'members',
        label: t('workspaceSwitcher.members'),
        disabled: current === null,
        onSelect: () => {
          if (current !== null) {
            onOpenMembers(current.id);
          }
        },
      },
      newWorkspaceItem,
    ];
  }

  return (
    <Menu
      align="start"
      sections={sections}
      footerItems={footerItems}
      {...(onCloseAutoFocus === undefined ? {} : { onCloseAutoFocus })}
      trigger={
        <button
          type="button"
          aria-label={accessibleName}
          className={cn(
            'flex h-9 min-w-0 shrink-0 items-center gap-1.5 rounded-card px-2 text-card text-ink',
            'hover:bg-surface-raised',
            className,
          )}
        >
          <Building2 aria-hidden="true" className="size-4 shrink-0 text-ink-muted" />
          {/*
            Visible label only — the accessible name lives on the button
            itself and does not change with it. Below 640px this span is
            hidden entirely (icon-only trigger). `inline-block`, not `inline`:
            `max-width`/`overflow` (and so `truncate`'s ellipsis) do not apply
            to non-replaced inline elements.
          */}
          <span
            aria-hidden="true"
            className="hidden truncate sm:inline-block sm:max-w-40 lg:max-w-64"
          >
            {triggerLabel}
          </span>
        </button>
      }
    />
  );
}
